import { ChevronDown, ChevronRight, EyeOff, Plus, Sparkles } from '@orgus/ui/icons';
import { useState } from 'react';
import { suggestionKey } from '@orgus/protocol';
import type { ProjectSuggestion } from '@orgus/protocol';
import { Button, ErrorState, GROUP_INDENT_SHIFT, IconButton, Pressable, RelativeTime, cn, plural, shortPath, toast } from '@orgus/ui';
import { runAction } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { useHiddenSuggestions } from './hidden.ts';
import { mockProjectAdded } from './mockEffects.ts';

/**
 * Repositories the agents found as the folder of a recent session: offered, never added on
 * their own. One quiet header row; the list opens on demand (and on first use, when there is no
 * project yet). Scratch folders are already left out (useVisibleSuggestions); each row can be
 * hidden for good.
 */
export function Suggestions({ suggestions, defaultOpen = false }: { suggestions: ProjectSuggestion[]; defaultOpen?: boolean }) {
  const machines = useOrgus((s) => s.machines);
  const hide = useHiddenSuggestions((s) => s.hide);
  const unhide = useHiddenSuggestions((s) => s.unhide);
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ key: string; error: unknown } | null>(null);
  const manyMachines = new Set(suggestions.map((s) => s.machineId)).size > 1 || Object.keys(machines).length > 1;

  const add = async (s: ProjectSuggestion) => {
    const key = suggestionKey(s);
    setBusy(key);
    setFailed(null);
    try {
      await runAction(s.machineId, 'project.add', { machineId: s.machineId, path: s.path });
      mockProjectAdded(s.machineId, s.path);
      toast.success(`${s.name} added`, `${machines[s.machineId]?.name ?? 'The machine'} is watching it now.`);
    } catch (e) {
      setFailed({ key, error: e });
    } finally {
      setBusy(null);
    }
  };

  const hideOne = (s: ProjectSuggestion) => {
    hide(s);
    toast('Suggestion hidden', { description: `${s.name} is no longer offered in this browser.`, action: { label: 'Undo', onClick: () => unhide(s) } });
  };

  return (
    <section aria-label="Found on your machines" className="overflow-hidden rounded-card border border-border bg-surface">
      <Pressable
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex h-row w-full items-center gap-2 px-row-x text-left hover:bg-hover"
      >
        <Sparkles className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <span className="shrink-0 text-ui font-medium text-fg">Found on your machines</span>
        <span className="hidden min-w-0 truncate text-caption text-fg-subtle num sm:inline">{plural(suggestions.length, 'repository', 'repositories')} from recent sessions</span>
        <span className="ml-auto flex items-center gap-1 text-caption text-fg-subtle">
          {open ? 'Hide list' : 'Review'}
          {open ? <ChevronDown className="size-4" aria-hidden /> : <ChevronRight className="size-4" aria-hidden />}
        </span>
      </Pressable>
      {open && (
        <ul className="border-t border-border-subtle">
          {suggestions.map((s) => {
            const key = suggestionKey(s);
            const m = machines[s.machineId];
            return (
              <li key={key} className="border-b border-border-subtle last:border-b-0">
                <div data-row="" className="group flex h-row items-center gap-3 px-row-x">
                  {/* One group step in: the name starts under the header's label (GROUP_INDENT_SHIFT). */}
                  <span className={cn('flex min-w-0 flex-1 items-baseline gap-3', GROUP_INDENT_SHIFT)}>
                    <span className="truncate text-ui font-medium text-fg">{s.name}</span>
                    {manyMachines && (
                      <span data-meta="" className="hidden shrink-0 text-caption text-fg-subtle sm:inline">
                        {m?.name ?? 'Removed machine'}
                      </span>
                    )}
                    <span data-meta="" className="hidden min-w-0 truncate font-mono text-caption text-fg-subtle md:inline">
                      {shortPath(s.path, 56)}
                    </span>
                  </span>
                  <span className="hidden shrink-0 text-caption text-fg-subtle num lg:inline">
                    <RelativeTime ts={s.lastSessionAt} fallback="" />
                  </span>
                  <IconButton label={`Hide ${s.name}`} icon={<EyeOff />} size="xs" onClick={() => hideOne(s)} />
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Plus />}
                    loading={busy === key}
                    disabled={busy != null && busy !== key}
                    onClick={() => add(s)}
                    aria-label={`Add ${s.name} on ${m?.name ?? 'its machine'}`}
                  >
                    Add
                  </Button>
                </div>
                {failed?.key === key && (
                  <div className={cn('px-row-x pb-3')}>
                    <ErrorState variant="inline" error={failed.error} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
